import React, { useState, useCallback } from 'react';
import AssistantPanel from './AssistantPanel';
import { useAssistantContext } from './useAssistantContext';
import { ASSISTANT_CHAT } from '../../config/endPoints';
import './assistant.scss';

let msgIdCounter = 0;
const makeId = () => String(++msgIdCounter);

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="white">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
    </svg>
  );
}

async function callAssistantAPI(message, file, context) {
  const formData = new FormData();
  formData.append('message', message);
  formData.append('context', JSON.stringify(context));
  if (file) formData.append('file', file);

  const token = localStorage.getItem('authorization') || '';

  // Do NOT set Content-Type — browser must auto-set multipart boundary
  const response = await fetch(ASSISTANT_CHAT, {
    method: 'POST',
    headers: { authorization: token },
    body: formData,
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.responseMessage || `Request failed (${response.status})`);
  }

  return response.json();
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);

  const { buildContext } = useAssistantContext();

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || loading) return;

    const userMsg = {
      id: makeId(),
      role: 'user',
      text,
      fileName: file ? file.name : null,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setError(null);

    const attachedFile = file;
    setFile(null);
    setLoading(true);

    try {
      const context = buildContext();
      const data = await callAssistantAPI(text, attachedFile, context);

      const reply = data?.data?.reply;
      const rem = data?.data?.remaining ?? null;

      if (!reply) throw new Error('No response from assistant.');

      setMessages((prev) => [
        ...prev,
        {
          id: makeId(),
          role: 'assistant',
          text: reply,
          fileName: null,
          timestamp: new Date(),
        },
      ]);

      if (rem !== null) setRemaining(rem);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [inputValue, file, loading, buildContext]);

  return (
    <div className="assistant-widget">
      {open && (
        <AssistantPanel
          onClose={() => setOpen(false)}
          messages={messages}
          loading={loading}
          error={error}
          remaining={remaining}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={handleSend}
          file={file}
          onFileChange={setFile}
          onFileRemove={() => setFile(null)}
        />
      )}

      <button
        className="assistant-fab"
        onClick={() => setOpen((o) => !o)}
        title="Ambaram Assistant"
        aria-label="Open fashion design assistant"
      >
        <ChatIcon />
      </button>
    </div>
  );
}
